import Guacamole from 'guacamole-common-js';
import React, { useEffect, useRef, useState } from 'react';
import CryptoJS from 'crypto-js';
import TrainerLoading from './TrainerLoading';

const SECRET_KEY = 'MySuperSecretKeyForParamsToken12';

function encryptToken(value) {
    const iv = CryptoJS.lib.WordArray.random(16);
    const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(value), key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
    });

    const data = {
        iv: CryptoJS.enc.Base64.stringify(iv),
        value: encrypted.toString(),
    };

    const json = JSON.stringify(data);
    return CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(json));
}

const TrainerGuacConnect = ({ details, onConnectionReady, refreshConnection, onRefreshComplete, disableCopyPaste, instanceStatus }) => {
    const { instance_username, public_ip_address, instance_password, ostype, guac_port } = details;
    const connectionType = ostype === 'windows' ? 'rdp' : 'ssh';

    const tokenObject = {
        connection: {
            type: connectionType,
            settings: {
                "hostname": public_ip_address,
                "username": instance_username,
                "password": instance_password,
                "ignore-cert": true,
                "security": "any",
                "port": guac_port,
                "width": "1370", // Adjust based on your RDP server's screen resolution
                "height": "515", // Adjust based on your RDP server's screen resolution
                "scrollback": "1000"
            }
        }
    };

    const displayRef = useRef(null);
    const guacRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);

    const initializeConnection = () => {
        const token = encryptToken(tokenObject);

        const NODE_URL = `${process.env.REACT_APP_NODE_URL}`;

        console.log("NODE_URL",NODE_URL);

        const tunnel = new Guacamole.WebSocketTunnel(NODE_URL);
        const client = new Guacamole.Client(tunnel);

        guacRef.current = client;

        if (displayRef.current) {
            displayRef.current.innerHTML = '';
            displayRef.current.appendChild(client.getDisplay().getElement());
        }

        client.connect('token=' + token);

        client.onstatechange = (state) => {
            console.log('Guacamole Client State:', state);
            if (state === 3) {
                setLoading(false);
                if (onConnectionReady) {
                    onConnectionReady();
                }
            } else if (state === 5) { // Disconnected
                setLoading(false);
            }
        };

        tunnel.onstatechange = (state) => {
            console.log('Tunnel State:', state);
            if (state === Guacamole.Tunnel.State.OPEN) {
                console.log('Tunnel opened.');
            } else if (state === Guacamole.Tunnel.State.CLOSED) {
                console.log('Tunnel closed.');
            }
        };

        tunnel.onerror = (error) => {
            console.error('Tunnel Error:', error);
        };

        return () => {
            client.disconnect();
        };
    };

    useEffect(() => {
        const cleanup = initializeConnection();

        return () => {
            if (guacRef.current) {
                guacRef.current.disconnect();
                guacRef.current = null;
            }
            cleanup();
        };
    }, [tokenObject, onConnectionReady]);

    useEffect(() => {
        if (refreshConnection) {
            if (guacRef.current) {
                guacRef.current.disconnect();
            }
            setLoading(true);
            initializeConnection();
            onRefreshComplete();
        }
    }, [refreshConnection]);

    useEffect(() => {
        if (!displayRef.current) return;

        displayRef.current.tabIndex = 0;
        displayRef.current.focus();
        const controlInput = true;
        if (!controlInput) {
            return;
        }

        const keyboard = new Guacamole.Keyboard(displayRef.current);
        const fixKeys = (keysym) => {
            if (keysym === 65508) return 65507;
            return keysym;
        };

        keyboard.onkeydown = function (keysym) {
            console.log("Key down:", keysym);
            guacRef.current?.sendKeyEvent(1, fixKeys(keysym));
        };

        keyboard.onkeyup = function (keysym) {
            console.log("Key up:", keysym);
            guacRef.current?.sendKeyEvent(0, fixKeys(keysym));
        };

        const mouse = new Guacamole.Mouse(displayRef.current);

        mouse.onmousemove = function (mouseState) {
            guacRef.current?.sendMouseState(mouseState);
        };

        mouse.onmousedown = mouse.onmouseup = function (mouseState) {
            guacRef.current?.sendMouseState(mouseState);
        };

        return () => {
            keyboard.onkeydown = null;
            keyboard.onkeyup = null;
            mouse.onmousemove = null;
            mouse.onmousedown = null;
            mouse.onmouseup = null;
        };
    }, [guacRef.current]);

    useEffect(() => {
        if (disableCopyPaste === 'yes') {
            return;
        }

        const handleServerClipboardChange = (stream, mimetype) => {
            if (document.activeElement !== displayRef.current) return;

            if (mimetype === "text/plain") {
                stream.onblob = (data) => {
                    let serverClipboard = atob(data);
                    if (serverClipboard.trim() !== "") {
                        navigator.clipboard.writeText(serverClipboard);
                    }
                };
            } else {
                console.log("Unsupported mime type:" + mimetype);
            }
        };

        const onFocusHandler = () => {
            navigator.clipboard.readText().then((clientClipboard) => {
                let stream = guacRef.current.createClipboardStream("text/plain", "clipboard");
                setTimeout(() => {
                    stream.sendBlob(btoa(unescape(encodeURIComponent(clientClipboard.replace(/[\r]+/gm, "")))));
                }, 200);
            });
        };

        if (navigator.clipboard) {
            displayRef.current.addEventListener("focus", onFocusHandler);
            guacRef.current.onclipboard = handleServerClipboardChange;
        }

        return () => {
            if (displayRef.current) {
                displayRef.current.removeEventListener("focus", onFocusHandler);
            }
        };
    }, [disableCopyPaste]);

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    return (
        <>
            {loading && <TrainerLoading />}
            <div ref={displayRef} style={{ width: '100%', height: '100%', overflow: 'hidden' }} />
        </>
    );
};

export default TrainerGuacConnect;
